import {Component, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TrainingService} from '../../../services/training.service';
import {forkJoin, from, Observable, of} from 'rxjs';
import {LoadingService} from '../../../services/loading.service';
import {shareReplay, switchMap, tap} from 'rxjs/operators';
import {FileUtilService} from '../../../services/file-util.service';
import {TrainingCompleteModel} from '../../../models/training/training-complete.model';
import {ToastrService} from 'ngx-toastr';
import {TranslateService} from '@ngx-translate/core';
import {AttendeeListComponent} from "./attendee-list/attendee-list.component";
import {TrainingFullModel} from "../../../models/training/training-full.model";
import {TrainingLocationTypeModel} from "../../../models/training/training-location-type.model";
import {RawFileModel} from "../../../models/raw-file-model";
import { UserContextService } from 'src/app/services/user-context.service';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-training-complete',
  templateUrl: './training-complete.component.html',
  styleUrl: './training-complete.component.scss',
  standalone: false
})
export class TrainingCompleteComponent {
  trainingId!: string;
  training$: Observable<TrainingFullModel>;

  formData: { memberList: File | null, meetingFiles: File[] } = {
    memberList: null, 
    meetingFiles: []
  };

  @ViewChild('attendeeList')
  attendeeList!: AttendeeListComponent;

  isFormSubmitting: boolean = false;
  isSuperAdmin: boolean = false;
  locationType: TrainingLocationTypeModel | null = null;

  showUnsavedIddTimeChangesModal: boolean = false;
  unsavedIddTimeChangesModalOptions: ConfirmationDialogData | null = null;
  
  readonly acceptedFileTypes = ".pdf,.png,.jpg,.jpeg,.xls,.xlsx";

  get isAllPersonsValid(): boolean {
    return this.attendeeList?.isAllPersonsValid;
  }

  get isOnlineMeetingType(): boolean {
    return this.locationType === TrainingLocationTypeModel.Web;
  }

  get isAttachmentMandatory() {
    if(this.allEventMembersWithESignature) 
      return false;
    
    return this.locationType == TrainingLocationTypeModel.Address || 
        this.locationType == TrainingLocationTypeModel.Web
  }

  constructor(
    private route: ActivatedRoute,
    private trainingService: TrainingService,
    private loadingService: LoadingService,
    private router: Router,
    private fileUtilService: FileUtilService,
    private toastrService: ToastrService,
    private translate: TranslateService,
    private userContext: UserContextService,
    private translateService: TranslateService,
    private toastService: ToastrService
  ) {
    this.trainingId = this.route.snapshot.params['id'];
    this.training$ = this.trainingService.getTraining(this.trainingId).pipe(shareReplay(1));
    this.isSuperAdmin = this.userContext.currentRole == 'SuperAdmin';
    this.training$.subscribe(t => {
      this.locationType= t.training.trainingLocation.type;
    })
  }

  onBackClick(): void {
    const hasAnyIddTimeChanged = 
      this.attendeeList.hasAnyIddTimeChanged();

    if(hasAnyIddTimeChanged) {
      this.openUnsavedIddTimeChangesModal();
    } else {
      this.redirectToTraining();
    }
  }

  private redirectToTraining() {
    this.router.navigate(['training', this.trainingId]);
  }

  get allEventMembersWithESignature(): boolean|undefined {
    if(!this.attendeeList) {
      return undefined;
    }

    return this.attendeeList.allParticipantsWithESignature();
  }

  get isAttachmentValid(): boolean {
    return !this.isAttachmentMandatory ||
          !!this.formData.memberList ||
          !!this.allEventMembersWithESignature;
  }

  onCompleteClick(): void {
    if (!this.isAttachmentValid) {
      this.translate.get('COMMON.FILE-NOT-SELECTED').subscribe(value => {
        this.toastrService.error(value);
      });
      return;
    }

    this.completeTraining();
  }

  openUnsavedIddTimeChangesModal() {
    this.showUnsavedIddTimeChangesModal = true;
    this.unsavedIddTimeChangesModalOptions = {
      title: this.translateService.instant(
        'UNSAVED-IDD-TIME-CHANGES.TITLE'
      ),
      message: this.translateService.instant(
        'UNSAVED-IDD-TIME-CHANGES.TEXT'
      ),
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.CONFIRM'),
      dismissButtonText: this.translateService.instant('COMMON.CANCEL'),
    };
  }

  saveIddTimeChanges(): void {
    const changedParticipants = this.attendeeList.selectedPersons
      .filter(p => p.iddTimeMinutes !== p.initialIddTimeMinutes)
      .map(p => ({
        personId: p.isUnknownUser ? null : p.id,
        eventMemberUnknownId: p.isUnknownUser
          ? (p.eventMemberUnknownId ?? null)
          : null,
        iddTimeMinutes: p.iddTimeMinutes
      }));

    if (changedParticipants.length === 0) {
      this.redirectToTraining();
      return;
    }

    this.loadingService
      .load(
        this.trainingService.updateIddTime(
          this.trainingId,
          changedParticipants
        )
      )
      .subscribe(() => {
        this.toastService.success(
          this.translateService.instant(
            'COMPLETE-TRAINING.IDD-TIME-CHANGES-SAVED'
          )
        );

        this.redirectToTraining();
      });
  }

  onShowUnsavedIddTimeChangesModalChanged(
    showUnsavedIddTimeChangesModal: boolean
  ) {
    this.showUnsavedIddTimeChangesModal = showUnsavedIddTimeChangesModal;

    if(!showUnsavedIddTimeChangesModal) {
      this.redirectToTraining();
    }
  }
  
  completeTraining() {
    this.isFormSubmitting = true;

    if(
      this.allEventMembersWithESignature && 
      !this.isOnlineMeetingType &&
      this.formData.memberList
    ) {
      this.formData.memberList = null;
    }
    
    const memberList$: Observable<RawFileModel | null> = this.formData.memberList 
      ? from(this.fileUtilService.convertFileToRawFileModel(this.formData.memberList)) 
      : of(null);

    const meetingFiles$: Observable<RawFileModel[]> =
      this.formData.meetingFiles && this.formData.meetingFiles.length
        ? forkJoin(
            this.formData.meetingFiles.map(file =>
              from(this.fileUtilService.convertFileToRawFileModel(file))
            )
          )
        : of([]);

    const uploadFiles$ = forkJoin({
      memberList: memberList$,
      meetingFiles: meetingFiles$
    });

    uploadFiles$.pipe(
      switchMap(({memberList, meetingFiles }) => {
        const trainingCompleteModel: TrainingCompleteModel = {
          persons: this.attendeeList.selectedPersons.map(person => ({
            personId: person.id,
            iddTimeMinutes: person.iddTimeMinutes
          })),
          memberList: memberList,
          meetingFiles: meetingFiles
        };

        return this.loadingService.load(
          this.trainingService.completeTraining(
            this.trainingId,
            trainingCompleteModel
          )
        );
      })
    )
    .subscribe({
      complete: () => {
        this.translate
          .get(
            'TRAINING.COMPLETE.TRAINING-COMPLETION-STARTED'
          )
          .subscribe(value => {
            this.toastrService.info(value);
            this.router.navigate(['/training']);
          });
      },
      error: () => {
        this.translate
          .get('COMMON.AN-ERROR-HAS-OCCURRED')
          .subscribe(value => {
            this.isFormSubmitting = false;
            this.toastrService.error(value);
          });
      }
    });
  }

  onMemberListSelected(file: File[]) {
    this.formData.memberList = file[0];
  }

  onDocumentSelected(files: File[]) {
    this.formData.meetingFiles.push(...files);
  }

  onRemoveDocument(index: number): void {
    this.formData.meetingFiles.splice(index, 1);
  }
}
